import type {
  DiagnosisCode,
  DocumentationStatus,
  ProcedureCode,
} from "../../../../global-providers/rxdb-provider/collections/epocus-billing";
import {
  type AortaWorksheetData,
  type BladderWorksheetData,
  type CardiacWorksheetData,
  type EFastWorksheetData,
  type ExamWorksheetData,
  isUsWorksheet,
  type LungWorksheetData,
  MSK_INDICATIONS,
  type MskIndication,
  type MskRegion,
  type MskSide,
  type MskWorksheetData,
  type PregnancyWorksheetData,
  type ProcedureWorksheetData,
  type RenalWorksheetData,
} from "../../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  MSK_REGION_CONFIG,
  mskBillingLevel,
  mskRegionData,
  scannedMskRegions,
} from "../worksheets/msk-regions";
import { procedureCodesFor } from "../worksheets/procedures/procedure-definitions";
import { CPT_DESCRIPTIONS } from "./cpt-descriptions";

// ─── Institution config (for future admin configurability) ───

export type InstitutionConfig = {
  // Some sites never bill 93306 from POCUS — only 93308
  allowCompleteEcho: boolean;
  // Some sites bundle eFAST as a single code rather than compositional billing
  allowMultiCodeFAST: boolean;
};

const DEFAULT_INSTITUTION_CONFIG: InstitutionConfig = {
  allowCompleteEcho: true,
  allowMultiCodeFAST: true,
};

// ─── CPT Suggestion Result ───

export type CptSuggestionResult = {
  procedureCode: ProcedureCode;
  description: string;
  // Advisory notes for the billing confirmation UI
  payerRiskNote?: string;
  completenessWarning?: string;
};

// ─── Per-exam CPT logic ───

const buildCode = (
  cpt: string,
  modifier: string | null,
  units: number,
  description: string,
  notes?: { payerRiskNote?: string; completenessWarning?: string },
  sideModifier: string | null = null,
): CptSuggestionResult => ({
  procedureCode: {
    cpt,
    modifier,
    sideModifier,
    units,
    systemSuggested: true,
    performerConfirmed: false,
    overrideReason: null,
  },
  description,
  ...notes,
});

const suggestCardiac = (
  data: CardiacWorksheetData,
  modifier: string | null,
  institutionConfig: InstitutionConfig,
): CptSuggestionResult[] => {
  const viewCount = Object.values(data.views).filter(Boolean).length;

  // Gap #2a: fewer than 2 views — surface a baseline code with a warning rather
  // than blocking entirely. The live chip must always show a code; the warning
  // appears in the billing confirmation sheet so the clinician is aware.
  if (viewCount < 2) {
    return [
      buildCode(
        "93308",
        modifier,
        1,
        "Limited echocardiography follow-up study",
        {
          completenessWarning:
            "Fewer than 2 views documented — standard practice requires ≥ 2 views for billing",
        },
      ),
    ];
  }

  const results: CptSuggestionResult[] = [];
  const allFourViews =
    data.views.plax &&
    data.views.psax &&
    data.views.apical4Chamber &&
    data.views.subxiphoid;

  // Gap #2c: complete echo upgrade — all 4 views + IVC measurement present
  if (
    allFourViews &&
    data.findings.ivcDiameterCm !== null &&
    institutionConfig.allowCompleteEcho
  ) {
    results.push(
      buildCode(
        "93306",
        modifier,
        1,
        "Complete transthoracic echocardiography",
      ),
    );
    // Replace 93308 with 93306 — intentional fall-through skips base push below
  } else {
    results.push(
      buildCode(
        "93308",
        modifier,
        1,
        "Limited echocardiography follow-up study",
      ),
    );
  }

  // Gap #2b: Doppler add-ons (alongside base code, not replacements)
  if (data.findings.spectralDoppler) {
    results.push(
      buildCode("93321", modifier, 1, "Doppler echocardiography; spectral"),
    );
  }
  if (data.findings.colorFlowDoppler) {
    results.push(
      buildCode("93325", modifier, 1, "Doppler echocardiography; color flow"),
    );
  }

  return results;
};

const suggestEfast = (
  data: EFastWorksheetData,
  modifier: string | null,
  institutionConfig: InstitutionConfig,
): CptSuggestionResult[] => {
  if (!institutionConfig.allowMultiCodeFAST) {
    // Fallback: single abdominal code
    return [buildCode("76705", modifier, 1, "Abdominal limited ultrasound")];
  }

  // Gap #3: compositional billing — only include codes for views actually captured
  const results: CptSuggestionResult[] = [];

  const hasAbdominal =
    data.views.ruq || data.views.luq || data.views.suprapubic;
  const hasCardiac = data.views.subxiphoid;
  const hasLung = data.views.rLung || data.views.lLung;

  if (hasAbdominal) {
    results.push(
      buildCode("76705", modifier, 1, "Abdominal limited ultrasound"),
    );
  }
  if (hasCardiac) {
    results.push(
      buildCode(
        "93308",
        modifier,
        1,
        "Limited echocardiography follow-up study",
      ),
    );
  }
  if (hasLung) {
    results.push(buildCode("76604", modifier, 1, "Chest ultrasound"));
  }

  // No views captured yet — return a baseline abdominal code so the live chip
  // is always visible. The code refines compositionally as views are added.
  if (results.length === 0) {
    return [
      buildCode("76705", modifier, 1, "Abdominal limited ultrasound", {
        completenessWarning:
          "No views documented yet — code will refine as views are recorded",
      }),
    ];
  }

  return results;
};

const suggestLung = (
  data: LungWorksheetData,
  modifier: string | null,
): CptSuggestionResult[] => {
  const hasRight =
    data.views.rUpperAnterior ||
    data.views.rLowerAnterior ||
    data.views.rPosterior;
  const hasLeft =
    data.views.lUpperAnterior ||
    data.views.lLowerAnterior ||
    data.views.lPosterior;

  // Gap #4: bilateral vs unilateral advisory
  const isBilateral = hasRight && hasLeft;
  const completenessWarning = !isBilateral
    ? "Unilateral assessment only — bilateral multi-zone exam strengthens billing eligibility"
    : undefined;

  return [
    buildCode("76604", modifier, 1, "Chest ultrasound", {
      payerRiskNote:
        "Payer-specific risk: some payers require bilateral multi-zone documentation. Clinician or coder may override.",
      completenessWarning,
    }),
  ];
};

const suggestRenal = (
  data: RenalWorksheetData,
  modifier: string | null,
): CptSuggestionResult[] => {
  // Gap #5: limited (76775) when only one kidney assessed or bladder not assessed
  const bothKidneysAssessed =
    (data.views.rKidneyLong || data.views.rKidneyTrans) &&
    (data.views.lKidneyLong || data.views.lKidneyTrans);
  const bladderAssessed =
    data.views.bladder || data.findings.bladderAssessed === true;

  const isLimited = !bothKidneysAssessed || !bladderAssessed;

  return isLimited
    ? [buildCode("76775", modifier, 1, "Retroperitoneal limited ultrasound")]
    : [buildCode("76770", modifier, 1, "Retroperitoneal complete ultrasound")];
};

const suggestAorta = (
  data: AortaWorksheetData,
  modifier: string | null,
): CptSuggestionResult[] => {
  const segments = [
    data.findings.proximal,
    data.findings.mid,
    data.findings.distal,
  ];

  // Gap #6: indication-driven code selection (not clinical setting)
  // Note: external billing guidance suggests 76775 for aorta limited; keeping
  // PRD codes (76706/76770) — do not change without clinical/billing sign-off.
  const cpt = data.indication === "aaa_screening" ? "76706" : "76770";
  const description =
    data.indication === "aaa_screening"
      ? "Aortic aneurysm screening"
      : "Retroperitoneal complete ultrasound";

  // When no diameter has been recorded yet, surface the baseline code with a
  // warning rather than blocking entirely. The live chip must always show.
  const anyDiameter = segments.some(
    (s) => s.diameterCm !== null && Number.isFinite(s.diameterCm),
  );
  if (!anyDiameter) {
    return [
      buildCode(cpt, modifier, 1, description, {
        completenessWarning:
          "No aortic diameter recorded — document measurements before submitting",
      }),
    ];
  }

  const allDiameters = segments.every(
    (s) => s.diameterCm !== null && Number.isFinite(s.diameterCm),
  );
  const completenessWarning = !allDiameters
    ? "One or more aortic segments missing diameter measurement"
    : undefined;

  return [buildCode(cpt, modifier, 1, description, { completenessWarning })];
};

const suggestBladder = (
  data: BladderWorksheetData,
  modifier: string | null,
): CptSuggestionResult[] => {
  // Gap #7: units = 2 when both pre-void and post-void measurements documented
  // Note: external guidance suggests 76857 for pelvic limited; keeping PRD code
  // (51798) — do not change without clinical/billing sign-off.
  const units =
    data.findings.bladderVolumeMl !== null &&
    data.findings.postVoidVolumeMl !== null
      ? 2
      : 1;

  return [
    buildCode("51798", modifier, units, "Bladder residual urine measurement"),
  ];
};

const MSK_PAYER_RISK_NOTE =
  "MSK coding is provisional pending clinical and billing sign-off. Clinician or coder may override.";

const MSK_GUIDED_PROCEDURE_NOTE =
  "Guided procedure: the procedure code belongs on the procedure note, and a diagnostic scan of the same region on the same day may not be separately billable. Confirm with a coder.";

const SIDE_MODIFIERS: Record<MskSide, (string | null)[]> = {
  right: ["-RT"],
  left: ["-LT"],
  bilateral: ["-RT", "-LT"],
};

const sideModifiersFor = (side: MskSide | null): (string | null)[] =>
  side ? SIDE_MODIFIERS[side] : [null];

export const suggestMskRegion = (
  data: MskWorksheetData,
  region: MskRegion,
  modifier: string | null,
): CptSuggestionResult[] => {
  const regionData = mskRegionData(data, region);
  const isGuided =
    regionData.indication !== null &&
    "guided" in MSK_INDICATIONS[regionData.indication];

  const isComplete = mskBillingLevel(data, region) === "complete";

  return sideModifiersFor(regionData.side).map((sideModifier) =>
    buildCode(
      isComplete ? "76881" : "76882",
      modifier,
      1,
      `${isComplete ? "Complete joint ultrasound" : "Limited extremity ultrasound"} — ${MSK_REGION_CONFIG[region].label}`,
      {
        payerRiskNote: isGuided
          ? `${MSK_PAYER_RISK_NOTE} ${MSK_GUIDED_PROCEDURE_NOTE}`
          : MSK_PAYER_RISK_NOTE,
      },
      sideModifier,
    ),
  );
};

export const suggestMskRegionCodes = (
  data: MskWorksheetData,
  modifier: string | null,
): Partial<Record<MskRegion, CptSuggestionResult>> =>
  Object.fromEntries(
    scannedMskRegions(data).flatMap((region) => {
      const [first] = suggestMskRegion(data, region, modifier);
      return first ? [[region, first]] : [];
    }),
  );

const suggestMsk = (
  data: MskWorksheetData,
  modifier: string | null,
): CptSuggestionResult[] => {
  const regions = scannedMskRegions(data);

  if (regions.length === 0) {
    return [
      buildCode("76882", modifier, 1, "Limited extremity ultrasound", {
        payerRiskNote: MSK_PAYER_RISK_NOTE,
        completenessWarning:
          "No region documented yet — code will refine as regions are recorded",
      }),
    ];
  }

  return regions.flatMap((region) => suggestMskRegion(data, region, modifier));
};

const suggestPregnancy = (
  data: PregnancyWorksheetData,
  modifier: string | null,
): CptSuggestionResult[] => {
  const tvViews =
    data.views.transvaginalSagittal || data.views.transvaginalTransverse;
  const taViews =
    data.views.transabdominalSagittal || data.views.transabdominalTransverse;

  // Transvaginal OB ultrasound takes precedence — 76817 (per gestation).
  // 76815 (limited transabdominal) is per-study and does not scale.
  // Clamp to a finite positive integer — the input is a numeric text field
  // and can yield NaN/0/non-integer values.
  const n = data.findings.numberOfGestations;
  const gestationUnits =
    n !== null && Number.isFinite(n) && Number.isInteger(n) && n >= 1 ? n : 1;
  if (tvViews) {
    return [
      buildCode(
        "76817",
        modifier,
        gestationUnits,
        "Ultrasound, pregnant uterus, transvaginal",
      ),
    ];
  }

  // Otherwise limited transabdominal OB — 76815 (per study, not per gestation).
  if (taViews) {
    return [
      buildCode(
        "76815",
        modifier,
        1,
        "Ultrasound, pregnant uterus, limited (quick look)",
      ),
    ];
  }

  // No views yet — surface the baseline limited code with a warning so the
  // live chip always renders.
  return [
    buildCode(
      "76815",
      modifier,
      1,
      "Ultrasound, pregnant uterus, limited (quick look)",
      {
        completenessWarning:
          "No views documented yet — code will refine as views are recorded",
      },
    ),
  ];
};

const PROCEDURE_PAYER_RISK_NOTE =
  "Procedure coding is provisional pending clinical and US coder sign-off. Clinician or coder may override.";

const ULTRASOUND_GUIDANCE_CPT = "76942";

const procedureSideModifiers = (side: unknown): (string | null)[] =>
  side === "bilateral" ? ["-RT", "-LT"] : [null];

const suggestProcedure = (
  data: ProcedureWorksheetData,
  modifier: string | null,
): CptSuggestionResult[] => {
  const note = { payerRiskNote: PROCEDURE_PAYER_RISK_NOTE };
  return procedureCodesFor(data).flatMap((cpt) =>
    cpt === ULTRASOUND_GUIDANCE_CPT
      ? [buildCode(cpt, modifier, 1, CPT_DESCRIPTIONS[cpt] ?? cpt, note)]
      : procedureSideModifiers(data.findings.side).map((sideModifier) =>
          buildCode(
            cpt,
            modifier,
            1,
            CPT_DESCRIPTIONS[cpt] ?? cpt,
            note,
            sideModifier,
          ),
        ),
  );
};

// ─── Public API ───

export const suggestCptCodes = (
  worksheetData: ExamWorksheetData,
  institutionConfig: InstitutionConfig = DEFAULT_INSTITUTION_CONFIG,
): CptSuggestionResult[] => {
  if (isUsWorksheet(worksheetData)) return [];

  const modifier = null;

  switch (worksheetData.examType) {
    case "cardiac":
      return suggestCardiac(worksheetData, modifier, institutionConfig);
    case "efast":
      return suggestEfast(worksheetData, modifier, institutionConfig);
    case "lung":
      return suggestLung(worksheetData, modifier);
    case "renal":
      return suggestRenal(worksheetData, modifier);
    case "aorta":
      return suggestAorta(worksheetData, modifier);
    case "bladder":
      return suggestBladder(worksheetData, modifier);
    case "pregnancy":
      return suggestPregnancy(worksheetData, modifier);
    case "msk":
      return suggestMsk(worksheetData, modifier);
    case "guided_joint_injection":
    case "guided_tendon_injection":
    case "guided_nerve_block":
      return suggestProcedure(worksheetData, modifier);
  }
};

// ─── Indication → ICD-10 Map ───
// Keyed by the snake_case enum value stored on worksheet data.
// Single source of truth: display name, ICD-10 code, and description in one place.

type IndicationIcd10Entry = {
  display: string;
  icd10Code: string;
  icd10Description: string;
};

// Derived from all worksheet indication types — compiler errors if a key is missing.
type Indication = Exclude<
  ExamWorksheetData["indication"],
  null | MskIndication | string[]
>;

const INDICATION_ICD10_MAP: Record<Indication, IndicationIcd10Entry> = {
  blunt_trauma: {
    display: "Blunt trauma",
    icd10Code: "S09.90XA",
    icd10Description: "Unspecified injury of head, initial encounter",
  },
  penetrating_trauma: {
    display: "Penetrating trauma",
    icd10Code: "T14.90XA",
    icd10Description: "Injury, unspecified, initial encounter",
  },
  hypotension: {
    display: "Hypotension",
    icd10Code: "R03.1",
    icd10Description: "Nonspecific low blood pressure reading",
  },
  dyspnea: {
    display: "Dyspnea",
    icd10Code: "R06.00",
    icd10Description: "Dyspnea, unspecified",
  },
  chest_pain: {
    display: "Chest pain",
    icd10Code: "R07.9",
    icd10Description: "Chest pain, unspecified",
  },
  arrest: {
    display: "Cardiac arrest",
    icd10Code: "I46.9",
    icd10Description: "Cardiac arrest, cause unspecified",
  },
  urinary_retention: {
    display: "Urinary retention",
    icd10Code: "R33.9",
    icd10Description: "Retention of urine, unspecified",
  },
  abdominal_pain: {
    display: "Abdominal pain",
    icd10Code: "R10.9",
    icd10Description: "Unspecified abdominal pain",
  },
  flank_pain: {
    display: "Flank pain",
    icd10Code: "N23",
    icd10Description: "Unspecified renal colic",
  },
  pulsatile_mass: {
    display: "Pulsatile abdominal mass",
    icd10Code: "I71.4",
    icd10Description: "Abdominal aortic aneurysm without rupture",
  },
  aaa_screening: {
    display: "AAA screening",
    icd10Code: "Z13.6",
    icd10Description: "Encounter for screening for cardiovascular disorders",
  },
  post_void_residual: {
    display: "Post void residual",
    icd10Code: "R33.9",
    icd10Description: "Retention of urine, unspecified",
  },
  hematuria: {
    display: "Hematuria",
    icd10Code: "R31.9",
    icd10Description: "Hematuria, unspecified",
  },
  aki: {
    display: "Acute kidney injury",
    icd10Code: "N17.9",
    icd10Description: "Acute kidney failure, unspecified",
  },
  syncope: {
    display: "Syncope",
    icd10Code: "R55",
    icd10Description: "Syncope and collapse",
  },
  trauma: {
    display: "Trauma",
    icd10Code: "S09.90XA",
    icd10Description: "Unspecified injury of head, initial encounter",
  },
  dating: {
    display: "Pregnancy dating",
    icd10Code: "Z36.89",
    icd10Description:
      "Encounter for other specified antenatal screening of mother",
  },
  vaginal_bleeding: {
    display: "Vaginal bleeding",
    icd10Code: "O20.9",
    icd10Description: "Haemorrhage in early pregnancy, unspecified",
  },
  pelvic_pain: {
    display: "Pelvic pain",
    icd10Code: "R10.2",
    icd10Description: "Pelvic and perineal pain",
  },
  suspected_ectopic: {
    display: "Suspected ectopic pregnancy",
    icd10Code: "O00.90",
    icd10Description:
      "Ectopic pregnancy, unspecified, without intrauterine pregnancy",
  },
  hyperemesis: {
    display: "Hyperemesis gravidarum",
    icd10Code: "O21.0",
    icd10Description: "Mild hyperemesis gravidarum",
  },
};

const isIndication = (key: string): key is Indication =>
  key in INDICATION_ICD10_MAP;

export const mapIndicationToDisplay = (
  indication: string | null,
): string | null => {
  if (!indication || !isIndication(indication)) return null;
  return INDICATION_ICD10_MAP[indication].display;
};

export const suggestDiagnosisCodes = (
  indication: string | null,
): DiagnosisCode[] => {
  if (!indication || !isIndication(indication)) return [];
  const entry = INDICATION_ICD10_MAP[indication];
  return [
    {
      code: entry.icd10Code,
      codingSystem: "ICD-10-CM",
      description: entry.icd10Description,
    },
  ];
};

// ─── Documentation Status Mapping ───

export const toDocumentationStatus = (
  engineStatus: "pass" | "block",
): DocumentationStatus => {
  switch (engineStatus) {
    case "pass":
      return "COMPLETE";
    case "block":
      return "INCOMPLETE";
  }
};

// ─── Documentation Sufficiency Validator ───

export type ValidationInput = {
  indication: string | null;
  imagesStored: boolean;
  findingsPopulated: boolean;
};

export type EngineValidationResult = {
  status: "pass" | "block";
  failures: string[];
};

export const validateDocumentation = ({
  indication,
  imagesStored,
  findingsPopulated,
}: ValidationInput): EngineValidationResult => {
  const blockers: string[] = [];

  if (!indication) {
    blockers.push("No clinical indication selected");
  }
  if (!imagesStored) {
    blockers.push("No images stored");
  }
  if (!findingsPopulated) {
    blockers.push("Required findings incomplete");
  }

  if (blockers.length > 0) {
    return { status: "block", failures: blockers };
  }

  return { status: "pass", failures: [] };
};

type SidedCode = { code: string; description: string };

const MSK_SIDED_ICD10: Partial<
  Record<
    MskIndication,
    {
      unspecified: SidedCode;
      right: SidedCode;
      left: SidedCode;
      bilateral?: SidedCode;
    }
  >
> = (() => {
  const sided = (
    unspecified: [string, string],
    right: [string, string],
    left: [string, string],
    bilateral?: [string, string],
  ) => ({
    unspecified: { code: unspecified[0], description: unspecified[1] },
    right: { code: right[0], description: right[1] },
    left: { code: left[0], description: left[1] },
    ...(bilateral && {
      bilateral: { code: bilateral[0], description: bilateral[1] },
    }),
  });
  const shoulderEffusion = sided(
    ["M25.419", "Effusion, unspecified shoulder"],
    ["M25.411", "Effusion, right shoulder"],
    ["M25.412", "Effusion, left shoulder"],
  );
  const shoulderPain = sided(
    ["M25.519", "Pain in unspecified shoulder"],
    ["M25.511", "Pain in right shoulder"],
    ["M25.512", "Pain in left shoulder"],
  );
  const kneeEffusion = sided(
    ["M25.469", "Effusion, unspecified knee"],
    ["M25.461", "Effusion, right knee"],
    ["M25.462", "Effusion, left knee"],
  );
  const kneePain = sided(
    ["M25.569", "Pain in unspecified knee"],
    ["M25.561", "Pain in right knee"],
    ["M25.562", "Pain in left knee"],
  );
  const ankleEffusion = sided(
    ["M25.473", "Effusion, unspecified ankle"],
    ["M25.471", "Effusion, right ankle"],
    ["M25.472", "Effusion, left ankle"],
  );
  const anklePain = sided(
    ["M25.579", "Pain in unspecified ankle and joints of unspecified foot"],
    ["M25.571", "Pain in right ankle and joints of right foot"],
    ["M25.572", "Pain in left ankle and joints of left foot"],
  );
  const lowerLegPain = sided(
    ["M79.669", "Pain in unspecified lower leg"],
    ["M79.661", "Pain in right lower leg"],
    ["M79.662", "Pain in left lower leg"],
  );
  const wristPain = sided(
    ["M25.539", "Pain in unspecified wrist"],
    ["M25.531", "Pain in right wrist"],
    ["M25.532", "Pain in left wrist"],
  );
  const foreignBodyCode: [string, string] = [
    "M79.5",
    "Residual foreign body in soft tissue",
  ];
  const foreignBody = sided(foreignBodyCode, foreignBodyCode, foreignBodyCode);
  return {
    shoulder_effusion_synovitis: shoulderEffusion,
    shoulder_rotator_cuff_tear: shoulderPain,
    shoulder_biceps_tendon: shoulderPain,
    shoulder_subacromial_bursitis: shoulderPain,
    shoulder_guided_procedure: shoulderPain,
    knee_effusion: kneeEffusion,
    knee_septic_arthritis: kneeEffusion,
    knee_bakers_cyst: kneePain,
    knee_extensor_tendon_injury: kneePain,
    knee_guided_procedure: kneePain,
    ankle_effusion: ankleEffusion,
    ankle_atfl_injury: anklePain,
    ankle_tendon_pathology: anklePain,
    ankle_guided_procedure: anklePain,
    achilles_rupture: lowerLegPain,
    achilles_tendinopathy: lowerLegPain,
    achilles_follow_up: sided(
      [
        "S86.019D",
        "Strain of unspecified Achilles tendon, subsequent encounter",
      ],
      ["S86.011D", "Strain of right Achilles tendon, subsequent encounter"],
      ["S86.012D", "Strain of left Achilles tendon, subsequent encounter"],
    ),
    wrist_tenosynovitis: wristPain,
    wrist_tendon_rupture: wristPain,
    wrist_ganglion: sided(
      ["R22.30", "Localized swelling, mass and lump, unspecified upper limb"],
      ["R22.31", "Localized swelling, mass and lump, right upper limb"],
      ["R22.32", "Localized swelling, mass and lump, left upper limb"],
      ["R22.33", "Localized swelling, mass and lump, upper limb, bilateral"],
    ),
    wrist_foreign_body: foreignBody,
    wrist_guided_injection: wristPain,
  };
})();

export const suggestMskDiagnosisCodes = (
  data: MskWorksheetData,
): DiagnosisCode[] => {
  const codes = scannedMskRegions(data).flatMap((region) => {
    const { indication, side } = mskRegionData(data, region);
    if (!indication) return [];
    const entry = MSK_SIDED_ICD10[indication];
    if (!entry) return [];
    const picked = !side
      ? [entry.unspecified]
      : side === "bilateral"
        ? entry.bilateral
          ? [entry.bilateral]
          : [entry.right, entry.left]
        : [entry[side]];
    return picked.map((sidedCode) => ({
      code: sidedCode.code,
      codingSystem: "ICD-10-CM" as const,
      description: sidedCode.description,
    }));
  });
  return codes.filter(
    (code, index) => codes.findIndex((c) => c.code === code.code) === index,
  );
};
